"use client"
import { useState } from "react"
import Link from "next/link"
import { SERP_COMPARISON_FIELDS, SERP_PROVIDERS } from "~/config/serp-comparison"
import { useTrackEvent } from "~/hooks/use-track-event"
import { ExternalLink } from "~/components/web/external-link"

export function SerpComparison() {
  const [selected, setSelected] = useState(SERP_PROVIDERS.map(p => p.slug))
  const [differences, setDifferences] = useState(false)
  const [interacted, setInteracted] = useState(false)
  const track = useTrackEvent()
  const providers = SERP_PROVIDERS.filter(p => selected.includes(p.slug))
  const fields = SERP_COMPARISON_FIELDS.filter(
    ([key]) => !differences || new Set(providers.map(p => p.facts[key].value)).size > 1,
  )
  return (
    <section
      aria-labelledby="serp-comparison-title"
      className="min-w-0 grid gap-4 rounded-lg border bg-card p-4 sm:p-6"
    >
      <div>
        <h2 id="serp-comparison-title" className="text-xl font-semibold">
          Compare Google SERP endpoints
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Compare request shape and operating model. Facts checked against vendor documentation on
          October 14, 2026. Performance and success rates have not been benchmarked.
        </p>
      </div>
      <fieldset className="flex flex-wrap gap-4">
        <legend className="mb-2 text-sm font-medium">Choose APIs</legend>
        {SERP_PROVIDERS.map(p => (
          <label key={p.slug} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selected.includes(p.slug)}
              className="size-4 accent-foreground"
              onChange={() => {
                setSelected(
                  selected.includes(p.slug)
                    ? selected.filter(s => s !== p.slug)
                    : [...selected, p.slug],
                )
                setInteracted(true)
                track("serp.comparison_changed", { provider: p.slug })
              }}
            />
            {p.name}
          </label>
        ))}
      </fieldset>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={differences}
          className="size-4 accent-foreground"
          onChange={e => {
            setDifferences(e.target.checked)
            setInteracted(true)
            track("serp.comparison_changed", { mode: "differences" })
          }}
        />
        Show differences only
      </label>
      {providers.length ? (
        <div
          tabIndex={0}
          role="region"
          aria-label="SERP comparison table, scroll horizontally"
          className="max-w-full overflow-x-auto rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="min-w-40 p-3">
                  Constraint
                </th>
                {providers.map(p => (
                  <th scope="col" key={p.slug} className="min-w-52 p-3">
                    <Link href={`/tools/${p.slug}`} className="underline underline-offset-4">
                      {p.name}
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(([key, label]) => (
                <tr key={key} className="border-t">
                  <th scope="row" className="p-3 align-top font-medium">
                    {label}
                  </th>
                  {providers.map(p => (
                    <td key={p.slug} className="max-w-64 break-words p-3 align-top">
                      <p>{p.facts[key].value}</p>
                      <details
                        className="mt-2 text-xs text-muted-foreground"
                        onToggle={event => {
                          if (event.currentTarget.open) {
                            setInteracted(true)
                            track("serp.comparison_field_viewed", { provider: p.slug, field: key })
                          }
                        }}
                      >
                        <summary className="cursor-pointer">Evidence</summary>
                        <p className="mt-1">
                          Vendor documentation · checked {p.facts[key].checkedAt}
                        </p>
                        <a
                          href={p.facts[key].sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-block text-xs text-muted-foreground underline underline-offset-2"
                          onClick={() => {
                            setInteracted(true)
                            track("serp.comparison_evidence_opened", {
                              provider: p.slug,
                              field: key,
                            })
                          }}
                        >
                          View documentation ↗
                        </a>
                      </details>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p role="status" className="text-sm">
          Select an API to compare.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {providers.map(p => (
          <div key={p.slug} className="rounded-md border p-3">
            <p className="font-medium">{p.name}</p>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              <ExternalLink
                href={p.website}
                doTrack
                toolSlug={p.slug}
                eventName="serp.provider_selected"
                eventProps={{ provider: p.slug, comparison_interacted: interacted }}
                className="underline underline-offset-4"
              >
                Visit {p.name} ↗
              </ExternalLink>
              <ExternalLink
                aria-label={`${p.name} pricing`}
                href={p.pricingUrl}
                className="underline underline-offset-4"
              >
                Current pricing ↗
              </ExternalLink>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Pricing depends on the plan, depth and execution mode. Check current vendor rates before
        purchasing. This table makes no cheapest or fastest claim.
      </p>
    </section>
  )
}
